<script lang="ts">
import { type MediaFile, MediaUtils } from '@monogrid/vue-boost/cms'
import MCmsImage, { type MCmsImageOptions } from '@monogrid/vue-boost/cms/MCmsImage.vue'
import { getTexture } from '@monogrid/vue-boost/three'
import { templateRef } from '@vueuse/core'
import gsap from 'gsap'
import { ComponentPublicInstance, computed, defineComponent, inject, PropType, watch } from 'vue'

import { lenisInjectionKey } from '@/App.vue'
import { useGlobalReducedMotion } from '@/functions/useGlobalReducedMotion'
import { useMediaMesh } from '@/functions/useMediaMesh'

export default defineComponent({
  name: 'LayerImage',
  components: {
    MCmsImage
  },
  props: {
    autoReveal: { type: Boolean, default: true },
    contain: { type: Boolean },
    cover: { type: Boolean },
    data: { type: Object as PropType<MediaFile>, required: true },
    hover: { type: Number, default: 0 },
    options: { type: Object as PropType<MCmsImageOptions>, default: () => ({ 'max-w': 1200 }) },
    order: { type: Number, default: 0 },
    revealMix: { type: Number, default: 0 },
    scale: { type: Number, default: 1 },
    squareSize: { type: Number, default: 3 },
    transparent: { type: Boolean },
    waveStrength: { type: Number, default: 1 },
    webgl: { type: Boolean, default: true },
    z: { type: Number, default: undefined }
  },
  setup (props, { attrs, slots, emit }) {
    const lenis = inject(lenisInjectionKey)
    const wrapper = templateRef<ComponentPublicInstance<typeof MCmsImage>>('wrapper')
    const reducedMotion = useGlobalReducedMotion()
    const mediaSize = computed<[number, number]>(() => ([MediaUtils.getWidth(props.data), MediaUtils.getHeight(props.data)]))
    const webglEnabled = computed(() => !!lenis.instance.value && props.webgl && !reducedMotion.value)

    const style = computed(() => ({ opacity: webglEnabled.value ? 0 : 2 - props.scale, '--scale': gsap.utils.mapRange(1, 2, 1, 1.3, props.scale) }))

    const { mesh } = useMediaMesh(wrapper, {
      autoReveal: () => props.autoReveal,
      contain: () => props.contain,
      cover: () => props.cover,
      enabled: webglEnabled,
      hover: () => props.hover,
      order: () => props.order,
      revealMix: () => props.revealMix,
      scale: () => props.scale,
      squareSize: () => props.squareSize,
      transparent: () => props.transparent,
      waveStrength: () => props.waveStrength,
      z: () => props.z,
      mediaSize
    })

    watch(() => wrapper.value?.url, (url) => {
      mesh.material.uniforms.tImg.value = url ? getTexture(wrapper.value?.url) : null
    }, { immediate: true })

    return {
      style
    }
  }
})
</script>

<template>
  <MCmsImage
    ref="wrapper"
    itemprop="image"
    class="layer-image overflow-hidden"
    :style="style"
    :data="data"
    :options="options"
    :contain="contain"
    :cover="cover"
    disable-optimization="gif"
  />
</template>

<style lang="scss" scoped>
.layer-image {
  ::v-deep(img) {
    transform: scale(var(--scale));
  }
}
</style>
